import { inject } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { FleetError } from '@4players/fleet';
import { AuthNoticeService } from 'app/core/auth/auth-notice.service';
import { AuthService } from 'app/core/auth/auth.service';
import { apiErrorMessage } from 'app/core/fleet/fleet-api-error-message';

/** Allow one reload per failing chunk URL and tab. */
const CHUNK_RELOAD_KEY = 'chunkReloadAttempt';

/** Recognize chunk-load failures separately from authentication errors. */
const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  if ((error as { name?: unknown }).name === 'ChunkLoadError') {
    return true;
  }

  const message = (error as { message?: unknown }).message;

  return (
    typeof message === 'string' &&
    /loading (css )?chunk|dynamically imported module|importing a module script failed/i.test(
      message,
    )
  );
};

/** Reload once to recover a stale build; skip recovery if storage is unavailable. */
const reloadForNewBuild = (url: string): void => {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === url) {
      return;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, url);
  } catch {
    return;
  }

  location.replace(url);
};

const FALLBACK =
  'Signed in, but the console could not be opened. Please try again.';

/** Where a Fleet failure sends the user instead of the sign-in page. */
export const SERVICE_UNAVAILABLE_PATH = '/service-unavailable';

const FLEET_FALLBACK = 'Fleet could not be reached. Please try again.';

/**
 * Recover current-session Fleet failures without ending a valid login.
 * Fleet requests retain their session version on the SDK error until navigation handles it.
 * Cancelled/obsolete work is ignored; other products retain their existing failure handling.
 */
export const sessionNavigationErrorHandler = (error: NavigationError): void => {
  const authService = inject(AuthService);

  if (
    !authService.fusionSid ||
    (error.error instanceof Error && error.error.name === 'AbortError')
  ) {
    return;
  }

  if (isChunkLoadError(error.error)) {
    reloadForNewBuild(error.url);
    return;
  }

  const authNoticeService = inject(AuthNoticeService);
  const router = inject(Router);

  if (error.error instanceof FleetError) {
    const version = (
      error.error as FleetError & { fleetSessionVersion?: number }
    ).fleetSessionVersion;
    if (version !== undefined && version !== authService.sessionVersion) return;

    authNoticeService.set(apiErrorMessage(error.error, FLEET_FALLBACK));
    router.navigateByUrl(
      `${SERVICE_UNAVAILABLE_PATH}?returnUrl=${encodeURIComponent(error.url)}`,
    );
    return;
  }

  authNoticeService.set(apiErrorMessage(error.error, FALLBACK));

  authService.signOut().subscribe(() => {
    router.navigateByUrl('/sign-in');
  });
};
